import { type VariantProps } from 'class-variance-authority'
import { SquareArrowOutUpRightIcon } from 'lucide-react'
import * as React from 'react'
import BaseMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'
import { remarkMark } from 'remark-mark-highlight'

import { cn } from '@/lib/ui/style-helpers'

import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'
import { LemaNewCapabilities } from '@/components/shared/markdown/custom-tags/lema-new-capabilities'
import { LemaPosture } from '@/components/shared/markdown/custom-tags/lema-posture'
import { LemaTag } from '@/components/shared/markdown/custom-tags/lema-tag'
import { defaultHeadingProse, markdownVariants } from '@/components/shared/markdown/variants'

// Backend-emitted custom tags. Extend: add to CUSTOM_TAGS, CUSTOM_TAG_ATTRS, customComponents.
const CUSTOM_TAGS = ['lema-tag', 'lema-posture', 'lema-new-capabilities'] as const
const CUSTOM_TAG_ATTRS: Record<(typeof CUSTOM_TAGS)[number], string[]> = {
  'lema-tag': ['variant'],
  'lema-posture': ['posture'],
  'lema-new-capabilities': [],
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), ...CUSTOM_TAGS],
  attributes: {
    ...defaultSchema.attributes,
    ...CUSTOM_TAG_ATTRS,
  },
}

const customComponents: Record<(typeof CUSTOM_TAGS)[number], React.ComponentType> = {
  'lema-tag': LemaTag,
  'lema-posture': LemaPosture,
  'lema-new-capabilities': LemaNewCapabilities,
}

type MarkdownHyperlinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement>

const TableColContext = React.createContext(-1)

type TableCellProps = { colIndex?: number }

const evidenceTableComponents = {
  tr: ({ children, ...props }: React.ComponentProps<'tr'>) => (
    <tr {...props}>
      {React.Children.map(children, (child, colIndex) =>
        React.isValidElement<TableCellProps>(child)
          ? React.cloneElement(child, { colIndex })
          : child,
      )}
    </tr>
  ),
  td: ({ colIndex, children, ...props }: React.ComponentProps<'td'> & TableCellProps) => (
    <TableColContext.Provider value={colIndex ?? -1}>
      <td {...props}>{children}</td>
    </TableColContext.Provider>
  ),
}

type MarkdownInlineHyperlinkProps = MarkdownHyperlinkProps & {
  showIcon?: boolean
}

const MarkdownInlineHyperlink = ({
  className,
  children,
  showIcon = true,
  ...props
}: MarkdownInlineHyperlinkProps) => (
  <Hyperlink
    className={cn('inline break-words underline-offset-2 hover:underline', className)}
    {...props}
  >
    {showIcon ? (
      <SquareArrowOutUpRightIcon
        aria-hidden
        className='mr-1 inline-block size-4 shrink-0 align-middle'
      />
    ) : null}
    {children}
  </Hyperlink>
)

export const MarkdownHyperlink = ({ className, children, ...props }: MarkdownHyperlinkProps) => {
  if (typeof children === 'string' && children.startsWith('^^') && children.endsWith('^^')) {
    return (
      <p>
        <Hyperlink className={cn('inline-flex items-center gap-2', className)} {...props}>
          <SquareArrowOutUpRightIcon className='size-4 shrink-0' />
          {children.slice(2, -2)}
        </Hyperlink>
      </p>
    )
  }

  return (
    <Hyperlink className={cn('inline-flex items-center gap-2', className)} {...props}>
      <SquareArrowOutUpRightIcon className='size-4 shrink-0' />
      {children}
    </Hyperlink>
  )
}

const MarkdownAnchor = ({
  firstTableColumnAsPlainText,
  ...anchorProps
}: React.ComponentProps<'a'> & { firstTableColumnAsPlainText: boolean }) => {
  const col = React.useContext(TableColContext)

  if (!firstTableColumnAsPlainText) {
    return <MarkdownHyperlink {...anchorProps} />
  }

  if (col === 0) {
    return <span>{anchorProps.children}</span>
  }

  return <MarkdownInlineHyperlink {...anchorProps} showIcon={false} />
}

const labelMarkdownClassName = 'm-0 text-main-foreground-primary !text-label-default'

const createLabelMarkdownHeading = (Tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => {
  const LabelMarkdownHeading = ({ children, ...props }: React.ComponentProps<'h1'>) => (
    <Tag className={labelMarkdownClassName} {...props}>
      {children}
    </Tag>
  )

  LabelMarkdownHeading.displayName = `LabelMarkdownHeading.${Tag}`

  return LabelMarkdownHeading
}

const MarkdownStrongAsLabel = ({ children, ...props }: React.ComponentProps<'strong'>) => (
  <strong className='!text-label-default' {...props}>
    {children}
  </strong>
)

const boldAsLabelComponents = {
  h1: createLabelMarkdownHeading('h1'),
  h2: createLabelMarkdownHeading('h2'),
  h3: createLabelMarkdownHeading('h3'),
  h4: createLabelMarkdownHeading('h4'),
  h5: createLabelMarkdownHeading('h5'),
  h6: createLabelMarkdownHeading('h6'),
  strong: MarkdownStrongAsLabel,
}

type MarkdownProps = React.ComponentProps<typeof BaseMarkdown> &
  VariantProps<typeof markdownVariants> & {
    className?: string
    /** Render markdown headings and **bold** as label typography. */
    boldAsLabel?: boolean
    /** Evidence tables: column 0 plain text; other columns inline links without icon. */
    firstTableColumnAsPlainText?: boolean
    bordered?: boolean
    allowRawHtml?: boolean
  }

export const Markdown = ({
  children,
  className,
  variant,
  boldAsLabel = false,
  firstTableColumnAsPlainText = false,
  bordered = false,
  allowRawHtml = false,
  ...props
}: MarkdownProps) => {
  const useDefaultHeadingProse = (variant ?? 'default') === 'default' && !boldAsLabel

  return (
    <div
      className={cn(
        markdownVariants({ variant, evidenceTable: firstTableColumnAsPlainText }),
        useDefaultHeadingProse && defaultHeadingProse,
        bordered &&
          '[&:not(:has(table))]:rounded-md [&:not(:has(table))]:border [&:not(:has(table))]:border-main-border-default [&:not(:has(table))]:p-4',
        className,
      )}
    >
      <BaseMarkdown
        components={{
          a: (anchorProps) => (
            <MarkdownAnchor
              {...anchorProps}
              firstTableColumnAsPlainText={firstTableColumnAsPlainText}
            />
          ),
          ...(allowRawHtml && customComponents),
          ...(boldAsLabel && boldAsLabelComponents),
          ...(firstTableColumnAsPlainText && evidenceTableComponents),
        }}
        remarkPlugins={[remarkGfm, remarkMark as () => undefined]} // this is remark's fault
        rehypePlugins={
          allowRawHtml ? [rehypeRaw, [rehypeSanitize, sanitizeSchema]] : [rehypeSanitize]
        }
        {...props}
      >
        {children}
      </BaseMarkdown>
    </div>
  )
}
